import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';

export default function ResetPassword() {
  const [searchParams]                    = useSearchParams();
  const token                             = searchParams.get('token');
  const navigate                          = useNavigate();
  const [password,  setPassword]          = useState('');
  const [confirm,   setConfirm]           = useState('');
  const [loading,   setLoading]           = useState(false);
  const [error,     setError]             = useState('');
  const [success,   setSuccess]           = useState(false);
  const [showPass,  setShowPass]          = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8)         { setError('Password must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(password))     { setError('Password must contain an uppercase letter.'); return; }
    if (!/[0-9]/.test(password))     { setError('Password must contain a number.'); return; }
    if (password !== confirm)        { setError('Passwords do not match.'); return; }
    if (!token)                      { setError('Invalid reset link. Please request a new one.'); return; }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.logo}>Qu<span style={{ color: '#4f6ef7' }}>vera</span></div>
          <h2 style={s.h2}>Invalid Link</h2>
          <p style={s.p}>This reset link is invalid or missing. Please request a new one.</p>
          <Link to="/forgot-password" style={s.btn}>Request New Link</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>Qu<span style={{ color: '#4f6ef7' }}>vera</span></div>

        {!success ? (
          <>
            <div style={s.iconWrap}>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <rect x="7" y="12" width="14" height="12" rx="2" stroke="#4f6ef7" strokeWidth="1.8"/>
                <path d="M10 12V9a4 4 0 018 0v3" stroke="#4f6ef7" strokeWidth="1.8" strokeLinecap="round"/>
                <circle cx="14" cy="17" r="1.5" fill="#4f6ef7"/>
              </svg>
            </div>
            <h2 style={s.h2}>Set new password</h2>
            <p style={s.p}>Choose a strong new password for your Quvera account.</p>

            {error && <div style={s.errorBox}>{error}</div>}

            <form onSubmit={submit} style={{ textAlign: 'left' }}>
              <div style={s.inputWrap}>
                <label style={s.label}>New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPass ? 'text' : 'password'}
                    placeholder="Min 8 chars, 1 uppercase, 1 number"
                    value={password} onChange={e => setPassword(e.target.value)}
                    style={s.input} required
                  />
                  <button type="button" onClick={() => setShowPass(v => !v)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9099b4', display: 'flex', alignItems: 'center' }}>
                    {showPass ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div style={s.inputWrap}>
                <label style={s.label}>Confirm New Password</label>
                <input
                  type="password" placeholder="Repeat your new password"
                  value={confirm} onChange={e => setConfirm(e.target.value)}
                  style={{ ...s.input, borderColor: confirm && confirm !== password ? '#dc2626' : '#e2e6f0' }}
                  required
                />
                {confirm && confirm !== password && (
                  <p style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>Passwords don't match</p>
                )}
              </div>
              {/* Password strength hints */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {[
                  [password.length >= 8,     '8+ characters'],
                  [/[A-Z]/.test(password),   'Uppercase'],
                  [/[0-9]/.test(password),   'Number'],
                  [password === confirm && password.length > 0, 'Passwords match'],
                ].map(([met, label]) => (
                  <span key={label} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: met ? '#dcfce7' : '#f8f9fc', color: met ? '#16a34a' : '#9099b4', border: `1px solid ${met ? '#86efac' : '#e2e6f0'}` }}>
                    {met ? '✓' : '○'} {label}
                  </span>
                ))}
              </div>
              <button type="submit" disabled={loading} style={s.btn}>
                {loading ? 'Resetting…' : 'Reset Password'}
              </button>
            </form>
          </>
        ) : (
          <>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M6 16l7 7 13-13" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 style={s.h2}>Password Reset! 🎉</h2>
            <p style={s.p}>Your password has been changed successfully. Redirecting you to login…</p>
            <Link to="/login" style={s.btn}>Go to Login →</Link>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page:     { minHeight: '100vh', background: '#f8f9fc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans',sans-serif", padding: 24 },
  card:     { background: '#fff', borderRadius: 20, padding: '40px 40px', maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(15,18,51,.08)', border: '1px solid #e2e6f0' },
  logo:     { fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1f5e', marginBottom: 24 },
  iconWrap: { width: 64, height: 64, borderRadius: '50%', background: '#eef1ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' },
  h2:       { fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1f5e', marginBottom: 10 },
  p:        { fontSize: 15, color: '#636b89', lineHeight: 1.65, marginBottom: 20 },
  errorBox: { background: '#fee2e2', color: '#dc2626', borderRadius: 10, padding: '11px 14px', fontSize: 14, marginBottom: 16, textAlign: 'left' },
  inputWrap:{ marginBottom: 16 },
  label:    { display: 'block', fontSize: 13, fontWeight: 600, color: '#2e3456', marginBottom: 6 },
  input:    { width: '100%', padding: '11px 14px', border: '1.5px solid #e2e6f0', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  btn:      { display: 'block', width: '100%', padding: '13px', background: '#4f6ef7', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 8, textDecoration: 'none', textAlign: 'center' },
};
